"use client";
import AllCategoriesButton from "../buttons/AllCategoriesButton";
import CategoriesButton from "../buttons/CategoriesButton";
import { useState, useMemo, useEffect, useRef } from "react";
import SubCategoriesButton from "../buttons/SubCategoriesButton";
import ProductCategoriesButton from "../buttons/ProductCategoriesButton";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../../shadcn/navigation-menu";
import useMenus from "@/app/lib/features/menus/menus.hook";
import AllCategoriesNavigationSkeleton from "../skeletons/AllCategoriesNavigationSkeleton";
import FallbackImage from "../FallbackImage";
import Link from "next/link";
import { trackInteraction } from "@/app/lib/tracking/interactions";

const AllCategoriesNavigation = () => {
  const { menus, isLoading, isError } = useMenus();
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [navValue, setNavValue] = useState<string>("");
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  // Panel yüksekliği: tasarım yüksekliği (680px) tavan, ama panelin ekranda
  // kalan alanından fazlasını asla almasın. Panelin üst offset'i header'ın
  // sticky durumuna göre değiştiği için sabit bir pay yerine gerçek konumu
  // ölçüyoruz; scroll iç kolonlarda (overflow-y-auto) oluşur.
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);

  useEffect(() => {
    if (navValue !== "categories") return;

    const measure = () => {
      const el = panelRef.current;
      if (!el) return;
      const available = window.innerHeight - el.getBoundingClientRect().top - 16;
      setPanelHeight(Math.max(240, Math.min(680, available)));
    };

    // Radix content'i açılış animasyonuyla mount ediyor; layout oturduktan
    // sonra ölç.
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [navValue]);

  const activeCategory = useMemo(() => {
    if (!menus || menus.length === 0) return null;
    return menus.find((c) => c.id === activeCategoryId) ?? menus[0];
  }, [menus, activeCategoryId]);

  if (!mounted) return <AllCategoriesButton />;

  // Skeleton: sadece aktif yükleniyorken VE veri henüz yoksa göster.
  // ISR cache'den gelen initialMenus zaten store'da olduğundan skeleton hiç görmeyiz.
  if (isLoading && !menus) {
    return (
      <NavigationMenu viewport={false}>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger className="p-0">
              <AllCategoriesButton />
            </NavigationMenuTrigger>
            <NavigationMenuContent className="p-0">
              <AllCategoriesNavigationSkeleton />
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    );
  }

  if (isError || !menus || menus.length === 0) {
    return null;
  }

  const hasChildren =
    activeCategory?.children && activeCategory.children.length > 0;

  const closeNav = () => setNavValue("");

  return (
    <NavigationMenu viewport={false} value={navValue} onValueChange={setNavValue}>
      <NavigationMenuList>
        <NavigationMenuItem value="categories">
          <NavigationMenuTrigger className="p-0">
            <AllCategoriesButton />
          </NavigationMenuTrigger>
          <NavigationMenuContent className="p-0">
            <div
              ref={panelRef}
              className="mega-menu-panel mx-auto flex overflow-hidden"
              style={panelHeight ? { height: panelHeight } : undefined}
            >
              <div className="min-w-48.75 bg-muted overflow-y-auto">
                {menus.map((category) => (
                  <div
                    key={category.id}
                    onMouseEnter={() => setActiveCategoryId(category.id)}
                  >
                    <CategoriesButton
                      active={category.id === activeCategory?.id}
                      category={category}
                      onClose={closeNav}
                    />
                  </div>
                ))}
              </div>

              <div className="grow self-stretch p-4 overflow-y-auto max-h-full">
                {hasChildren ? (
                  <div className="flex flex-col gap-6">
                    {activeCategory!.children.map((sub, i) => (
                      <div key={i} className="space-y-3">
                        <SubCategoriesButton category={sub} onClose={closeNav} />
                        {sub.children && sub.children.length > 0 && (
                          <div className="grid grid-cols-8 gap-3">
                            {sub.children.map((product, j) => (
                              <ProductCategoriesButton
                                key={j}
                                product={product}
                                onClose={closeNav}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  activeCategory && (
                    <Link
                      href={`/${activeCategory.url}`}
                      onClick={() => { trackInteraction("menu", activeCategory.id, "click"); closeNav(); }}
                      className="flex flex-col items-center justify-center h-full gap-4 group"
                    >
                      <div className="relative w-64 h-64 rounded-xl overflow-hidden">
                        <FallbackImage
                          src={activeCategory.image_url}
                          alt={activeCategory.name}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                          sizes="256px"
                        />
                      </div>
                      <p className="text-lg font-semibold text-main group-hover:underline">
                        {activeCategory.page_title ?? activeCategory.name}
                      </p>
                    </Link>
                  )
                )}
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default AllCategoriesNavigation;
